import type { StoreActionApi } from 'react-sweet-state';
import type { IntlShape, MessageDescriptor } from 'react-intl';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert/quick-insert-plugin-type';

import type { SnippetsPlugin } from '@atlassian/editor-plugin-snippets';

import { expValEquals } from '@confluence/feature-experiments';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import type { CreateSnippetPayload } from '../../services/types/blockServiceTypes.ts';
import { isBodyTooLargeError } from '../../services/types/blockServiceTypes.ts';
import { messages as baseMessages } from '../../modals/base/SnippetBaseModal.i18n.ts';
import { addSnippetQuickInsertItem } from '../../quick-insert/snippetQuickInsertItemApi.tsx';
import { mapConfluenceSnippet } from '../../provider/mapConfluenceSnippet.ts';
import type { Snippet } from '../../types/snippet.ts';
import { SnippetBodyFormat } from '../../types/snippetBodyFormat.ts';
import { SnippetProduct } from '../../types/snippetProduct.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';

import { shareActions, type ShareSnippetOptions } from './shareActions.ts';

/**
 * Returns the appropriate error flag title and description MessageDescriptors based on whether
 * the error is a body-too-large error. Uses shared base messages for too-large errors and
 * falls back to the provided action-specific messages otherwise.
 */
export const getSnippetErrorFlagMessages = (
	error: Error,
	actionErrorFlagTitle: MessageDescriptor,
	actionErrorFlagDescription: MessageDescriptor,
): { flagTitle: MessageDescriptor; flagDescription: MessageDescriptor } => {
	const isBodyTooLarge = isBodyTooLargeError(error);
	return {
		flagTitle: isBodyTooLarge ? baseMessages.contentTooLargeFlagTitle : actionErrorFlagTitle,
		flagDescription: isBodyTooLarge
			? baseMessages.contentTooLargeFlagDescription
			: actionErrorFlagDescription,
	};
};

/**
 * Builds a CreateSnippetPayload for the create snippet API from name, ADF body, and optional description.
 */
export function buildCreatePayload(
	name: string,
	body: ADFEntity,
	description?: string,
): CreateSnippetPayload {
	const descriptionValue = description?.trim();
	return {
		name,
		...(descriptionValue && { description: descriptionValue }),
		body: JSON.stringify(body),
		bodyFormat: SnippetBodyFormat.ADF,
		product: SnippetProduct.CONFLUENCE,
	};
}

type SnippetEditorAPI = PublicPluginAPI<[QuickInsertPlugin, SnippetsPlugin]>;

export type AddSnippetToQuickInsertAndShareOptions = Pick<ShareSnippetOptions, 'flags' | 'intl'> & {
	editorAPI?: SnippetEditorAPI | null;
	isShared?: boolean;
};

export type AddSnippetToQuickInsertAndShareParams = {
	snippet: Snippet;
	options: AddSnippetToQuickInsertAndShareOptions;
	storeApi: StoreActionApi<SnippetsState>;
	onShareFailure?: (snippetId: string, error: Error) => void;
};

/**
 * Adds snippet to quick insert when editorAPI is set, and shares the snippet when site sharing
 * is enabled and isShared is true. Call after create or duplicate succeeds.
 */
export async function addSnippetToQuickInsertAndShare({
	snippet,
	options,
	storeApi,
	onShareFailure,
}: AddSnippetToQuickInsertAndShareParams): Promise<void> {
	const { getState, dispatch } = storeApi;
	const { flags, intl, editorAPI, isShared } = options;
	if (editorAPI) {
		if (expValEquals('platform_editor_snippets_plugin_confluence', 'isEnabled', true)) {
			const reconcileSnippet = editorAPI.snippets?.commands.reconcileSnippet;
			if (reconcileSnippet) {
				editorAPI.core.actions.execute(
					reconcileSnippet({
						snippet: {
							...mapConfluenceSnippet(snippet),
							isCreatedByCurrentUser: true,
						},
					}),
				);
			}
		} else {
			addSnippetQuickInsertItem({ snippet, editorAPI });
		}
	}
	if (getState().isSnippetSharingForSiteEnabled && isShared) {
		await dispatch(
			shareActions.shareSnippet(snippet.snippetId, {
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				onShareFailure,
			}),
		);
	}
}

export type CreateSnippetOptions = {
	flags: WithFlagsProps['flags'];
	intl: IntlShape;
	editorAPI?: SnippetEditorAPI | null;
	isShared?: boolean;
	onShareFailure?: (snippetId: string, error: Error) => void;
	onSaveFailure?: (error: Error) => void;
	successFlagActions?: Parameters<WithFlagsProps['flags']['showSuccessFlag']>[0]['actions'];
	/** When false, the success flag is not shown (e.g. when showing FirstSnippetCreatedModal instead). Defaults to true. */
	showSuccessFlag?: boolean;
	messages?: SnippetSaveMessages;
};

export type SnippetSaveMessages = {
	successFlagTitle: MessageDescriptor;
	errorFlagTitle: MessageDescriptor;
	errorFlagDescription: MessageDescriptor;
};
